import { BaseDAO } from './baseDAO';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { HttpHeaders } from '@angular/common/http';
import { ActionType } from 'anchor-shared/lib/model/interface/actionTrigger';
import { GroupParticipants } from 'anchor-shared/lib/model/interface/cometChatGroupParticipant';
import { CometChatGroup } from 'anchor-shared/lib/model/interface/cometChatGroup';
import { CometChatMessage } from 'anchor-shared/lib/model/interface/cometChatMessage';
import { CharterStatus } from 'anchor-shared/lib/model/enum/charter-status';

@Injectable()
export class CometChatDAO extends BaseDAO<string> {
  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.COMETCHAT_MESSAGING);
  }

  public searchConversations(
    searchString: string,
    userId: string | number,
    page: number = 1,
    limit: number = 18,
  ): Observable<CometChatGroup[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/search-conversations?userId=${userId}&searchString=${searchString}&page=${page}&limit=${limit}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<CometChatGroup[]>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public createGroup(
    guid: string,
    groupParticipants: GroupParticipants,
  ): Observable<CometChatGroup> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/group/create';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const body = {
      guid,
      name,
      groupParticipants,
    };
    return this.apiHelper.apiCall<CometChatGroup>(
      url,
      method,
      null,
      body,
      headers,
    );
  }

  public updateGroupCharterStatus(
    guid: string | number,
    role: 'admin' | 'owner' | 'renter',
    sourceFunction: string,
    status?: CharterStatus,
    action?: ActionType,
  ): Observable<CometChatGroup | void> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/group/${guid}/charter-status?role=${role}&sourceFunction=${sourceFunction}`;
    if (status) {
      url = url + `&status=${status}`;
    }
    if (action) {
      url = url + `&action=${action}`;
    }
    const method = 'PATCH';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<CometChatGroup | void>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public ensureGroupExists(guid: string | number): Observable<CometChatGroup> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/group/ensure-exists/${guid}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<CometChatGroup>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public ensureCometChatUsersExist(
    userId: number,
  ): Observable<{ success: boolean }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/user/exists/${userId}`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ success: boolean }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public addUserToGroup(
    guid: string,
    userId: string,
  ): Observable<{ success: boolean }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/group/${guid}/add/${userId}`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ success: boolean }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public removeUserFromGroup(
    guid: string,
    userId: string,
  ): Observable<CometChatMessage> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/group/${guid}/remove/${userId}`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<CometChatMessage>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public updateUserProfileImg(
    uid: string | number,
    imgPath: string,
  ): Observable<{ success: boolean }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/user/${uid}/profile-img`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ success: boolean }>(
      url,
      method,
      null,
      {
        imgPath,
      },
      headers,
    );
  }

  public deactivateGroup(
    guid: string | number,
  ): Observable<{ success: boolean }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/remove-super-admin/${guid}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ success: boolean }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  public reactivateGroup(
    guid: string | number,
  ): Observable<{ success: boolean }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      `/add-super-admin/${guid}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<{ success: boolean }>(
      url,
      method,
      null,
      null,
      headers,
    );
  }
}
